import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import Heading from "../../../../../../common/element/Heading";
import BtnLight from "../../../../../../common/element/Buttons/btnLight";
import BtnPrimary from "../../../../../../common/element/Buttons/btnPrimary";
import AllProviderMultiSelectDropdown from "../../../../../../common/MultiSelectFilter/AllProviderMultiSelectDropdown";
import ProvideTyperMultiSelectDropdown from "../../../../../../common/MultiSelectFilter/ProvideTyperMultiSelectDropdown";
import ThreeDotBouncing from "../../../../../../common/element/PageLoader";
import { setBreadcrumbs } from "../../../../../../redux/classes/breadcrumbsSlice";
import useUnauthorizedHandler from "../../../../../../common/snippet/useUnauthorizedHandler";
import DeactivatedPaymentsList from "./DeactivatedPaymentsList";
import { CSVLink } from "react-csv";
import {
  GenerateLicensingGracePeriodFollowUpsReport,
} from "../../../../../../actions/Admin/reports/GenerateLicensingGracePeriodFollowUpsReport";

const normaliseSelection = (list = []) =>
  list
    .map((item) =>
      item?.provider_org_id ??
      item?.organisation_id ??
      item?.provider_type_id ??
      item?.value ??
      item
    )
    .filter((entry) => entry !== undefined && entry !== null);

const DeactivatedPaymentsReport = () => {
  const dispatch = useDispatch();
  const handleUnauthorizedAccess = useUnauthorizedHandler();
  const csvLinkRef = useRef(null);

  const [reportData, setReportData] = useState({ rows: [], metadata: {} });
  const [isLoading, setIsLoading] = useState(false);

  const [filters, setFilters] = useState({
    provider_org_ids: [],
    provider_type_ids: [],
  });

  const latestFiltersRef = useRef(filters);
  const hasFetchedInitialRef = useRef(false);

  const normalizedFilters = useMemo(() => {
    const providerIds = Array.isArray(filters.provider_org_ids)
      ? normaliseSelection(filters.provider_org_ids)
      : [];
    const providerTypeIds = Array.isArray(filters.provider_type_ids)
      ? normaliseSelection(filters.provider_type_ids)
      : [];

    const base = {
      provider_org_ids: providerIds,
      provider_type_ids: providerTypeIds,
    };

    return Object.entries(base).reduce((accumulator, [key, value]) => {
      if (Array.isArray(value) && value.length) {
        accumulator[key] = value;
      }
      return accumulator;
    }, {});
  }, [filters]);

  const extractPayload = useCallback((payload) => {
    if (!payload) {
      return { rows: [], metadata: {} };
    }

    const base = payload?.data ?? payload;
    const rows = Array.isArray(base?.rows) ? base.rows : [];
    const metadata =
      base?.metadata && typeof base.metadata === "object" ? base.metadata : {};

    return { rows, metadata };
  }, []);

  const fetchFollowUps = useCallback(
    async (activeFilters) => {
      if (!activeFilters) {
        return null;
      }

      setIsLoading(true);
      try {
        const response = await dispatch(
          GenerateLicensingGracePeriodFollowUpsReport(activeFilters)
        );

        const successStatuses = new Set(["GETSUCCESS", "SUCCESS", "OK"]);
        if (response?.success === true || successStatuses.has(response?.status)) {
          const parsed = extractPayload(response);
          setReportData(parsed);
        } else if (response?.status === 401) {
          handleUnauthorizedAccess();
          setReportData({ rows: [], metadata: {} });
        } else {
          console.warn("Licensing follow-ups returned unexpected response", response);
          setReportData({ rows: [], metadata: {} });
        }

        return response;
      } catch (error) {
        console.error("Error fetching Licensing follow-ups report:", error);
        setReportData({ rows: [], metadata: {} });
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, handleUnauthorizedAccess, extractPayload]
  );

  useEffect(() => {
    dispatch(
      setBreadcrumbs([
        {
          label: "Reports",
          url: "/admin/reports",
          home: "/admin/dashboard",
        },
        {
          label: "Deactivated payments",
          url: "/admin/reports/deactivated-payments",
        },
      ])
    );
  }, [dispatch]);

  useEffect(() => {
    if (!hasFetchedInitialRef.current) {
      hasFetchedInitialRef.current = true;
      latestFiltersRef.current = normalizedFilters;
      fetchFollowUps(normalizedFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const nextFilters = JSON.stringify(normalizedFilters);
    if (JSON.stringify(latestFiltersRef.current) === nextFilters) {
      return;
    }
    latestFiltersRef.current = JSON.parse(nextFilters);
    fetchFollowUps(latestFiltersRef.current);
  }, [normalizedFilters, fetchFollowUps]);

  const updateFilters = useCallback((field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const refreshReport = () => {
    fetchFollowUps(latestFiltersRef.current);
  };

  const rows = Array.isArray(reportData?.rows) ? reportData.rows : [];

  const csvData = useMemo(() => {
    if (!rows.length) {
      return [];
    }
    return rows.map((row) => ({
      "Provider name": row?.provider_trading_name ?? row?.provider_name ?? "-",
      "Provider type": row?.provider_type_name ?? row?.provider_type ?? "-",
      "Licence expires": row?.licence_expiry_date ?? row?.expiry_date ?? "",
      "Grace period ends": row?.grace_period_end_date ?? row?.grace_end_date ?? "",
      "Follow-up owner": row?.follow_up_owner_name ?? row?.owner_name ?? "-",
      "Follow-up status": row?.follow_up_status ?? row?.status ?? "-",
      "Follow-up notes": row?.follow_up_notes ?? row?.notes ?? "-",
      "Outstanding balance": row?.outstanding_balance ?? row?.overdue_amount ?? "",
      "Last contacted": row?.last_contacted_on ?? "",
    }));
  }, [rows]);

  const handleExport = () => {
    if (!csvData.length || !csvLinkRef.current) {
      return;
    }

    csvLinkRef.current.link.click();
  };

  const hasResults = rows.length > 0;

  return (
    <div className="px-5 py-6 lg:px-10 lg:py-8">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <Heading
          title="Deactivated payments"
          subtitle="Licences that lapsed and require follow-up"
        />
        <div className="flex gap-3">
          <BtnLight
            className="min-w-[120px]"
            onClick={refreshReport}
            disabled={isLoading}
          >
            Refresh
          </BtnLight>
          <BtnPrimary
            className="min-w-[160px]"
            onClick={handleExport}
            disabled={isLoading || !csvData.length}
          >
            Export CSV
          </BtnPrimary>
          <CSVLink
            data={csvData}
            filename="deactivated-payments.csv"
            ref={csvLinkRef}
            style={{ display: "none" }}
          />
        </div>
      </div>

      <div className="mt-6 grid gap-4 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-gray-700">Provider</span>
            <AllProviderMultiSelectDropdown
              title="Provider"
              updateFilter={(field, value) => updateFilters(field, value)}
              Path="provider"
              fieldName="provider_org_ids"
            />
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-gray-700">Provider type</span>
            <ProvideTyperMultiSelectDropdown
              title="Provider type"
              updateFilter={(field, value) => updateFilters(field, value)}
              provider_org_ids={Array.isArray(filters.provider_org_ids) ? filters.provider_org_ids : []}
            />
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-gray-200 bg-white">
        {isLoading && !hasResults ? (
          <div className="flex justify-center py-16">
            <ThreeDotBouncing />
          </div>
        ) : (
          <div className="p-5">
            <DeactivatedPaymentsList rows={rows} />
          </div>
        )}
      </div>
    </div>
  );
};

export default DeactivatedPaymentsReport;
